/* Peluche Kingdom : pilotage des pistes de rayon (P1.25, 02/08/2026).

   Volontairement independant de slider-component de Dawn : ce dernier place ses commandes
   SOUS la piste et compte les diapositives, alors qu'un rayon veut des fleches posees sur les
   bords. Le defilement lui-meme reste natif (overflow-x + scroll-snap), donc la piste
   fonctionne deja sans ce fichier : le JS n'ajoute que les fleches et leur etat.

   ⚠️ Les fleches ne s'affichent QUE si la piste deborde reellement. Une collection de quatre
   produits sur un ecran large n'a rien a faire defiler, et une fleche morte est un defaut
   visible. */

class PkRayonPiste extends HTMLElement {
  connectedCallback() {
    this.piste = this.querySelector('.pk-rayon__piste');
    if (!this.piste) return;

    this.boutons = Array.from(this.querySelectorAll('.pk-rayon__fleche'));
    this.boutons.forEach((b) => b.addEventListener('click', () => this.pousser(b.dataset.sens)));

    // Le scroll natif est bruyant : on n'y reagit qu'une fois la frame suivante peinte.
    this.piste.addEventListener('scroll', () => this.planifier(), { passive: true });

    if ('ResizeObserver' in window) {
      this.observateur = new ResizeObserver(() => this.planifier());
      this.observateur.observe(this.piste);
    } else {
      window.addEventListener('resize', () => this.planifier());
    }

    this.suivreImages();

    // Rayon differe : les cartes attendent dans un <template>, donc hors du DOM et sans
    // telecharger leurs images. On ne les pose qu'a l'approche du rayon.
    this.reserve = this.querySelector('template.pk-rayon__reserve');
    if (this.reserve) this.armerHydratation();

    this.rafraichir();
  }

  armerHydratation() {
    // Pas d'IntersectionObserver (vieux navigateur, ou robot qui execute le JS sans
    // defiler) : on pose tout de suite plutot que de laisser une piste vide.
    if (!('IntersectionObserver' in window)) {
      this.hydrater();
      return;
    }

    // 800 px d'avance : les cartes sont en place avant d'entrer dans l'ecran, donc la
    // substitution ne se voit pas.
    this.veille = new IntersectionObserver(
      (entrees) => {
        if (entrees.some((e) => e.isIntersecting)) this.hydrater();
      },
      { rootMargin: '800px 0px' },
    );
    this.veille.observe(this);
  }

  hydrater() {
    if (!this.reserve) return;
    if (this.veille) this.veille.disconnect();

    this.piste.replaceChildren(this.reserve.content.cloneNode(true));
    this.piste.classList.remove('pk-rayon__piste--attente');
    this.reserve.remove();
    this.reserve = null;

    this.suivreImages();
    this.rafraichir();
  }

  /** Les images arrivent apres le HTML et changent la largeur utile de la piste : sans ce
   *  recalcul, une piste jugee non debordante au chargement garde ses fleches masquees. */
  suivreImages() {
    this.piste.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', () => this.planifier(), { once: true });
    });
  }

  disconnectedCallback() {
    if (this.observateur) this.observateur.disconnect();
    if (this.veille) this.veille.disconnect();
  }

  planifier() {
    if (this.attente) return;
    this.attente = requestAnimationFrame(() => {
      this.attente = null;
      this.rafraichir();
    });
  }

  /** Largeur d'un saut : autant de cartes entieres que l'ecran en montre. */
  pas() {
    const carte = this.piste.querySelector('.pk-rayon__carte');
    if (!carte) return this.piste.clientWidth;

    const styles = getComputedStyle(this.piste);
    const espace = parseFloat(styles.columnGap) || 0;
    const unite = carte.getBoundingClientRect().width + espace;
    if (unite <= 0) return this.piste.clientWidth;

    const visibles = Math.max(1, Math.floor(this.piste.clientWidth / unite));
    return visibles * unite;
  }

  pousser(sens) {
    const doux = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.piste.scrollBy({ left: sens === 'prec' ? -this.pas() : this.pas(), behavior: doux });
  }

  rafraichir() {
    const cadre = this.closest('.pk-rayon__cadre') || this;
    // 2 px de tolerance : les largeurs sous-pixel font conclure a un debordement inexistant.
    const deborde = this.piste.scrollWidth - this.piste.clientWidth > 2;
    cadre.classList.toggle('pk-rayon__cadre--defilable', deborde);

    const gauche = this.piste.scrollLeft;
    const max = this.piste.scrollWidth - this.piste.clientWidth;

    this.boutons.forEach((b) => {
      const audebut = b.dataset.sens === 'prec' ? gauche <= 2 : gauche >= max - 2;
      b.hidden = !deborde || audebut;
    });
  }
}

if (!customElements.get('pk-rayon-piste')) {
  customElements.define('pk-rayon-piste', PkRayonPiste);
}
